import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { Listing } from '../types';
import Button from '../components/UI/Button';
import { useAuth } from '../context/AuthContext';
import { MapPin, Calendar, Eye, Share2, AlertCircle, Phone, MessageCircle, Check, DollarSign, Users, Briefcase, TrendingUp, Clock, Award, Coins, Heart, FileText, BarChart3, ShieldCheck } from 'lucide-react';
import { TRL_LEVELS } from '../constants';
import ImageLightbox from '../components/ImageLightbox';

const ListingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [listing, setListing] = useState<Listing | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  
  const [startingChat, setStartingChat] = useState(false);
  const [showContactBox, setShowContactBox] = useState(false);

  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Unit Purchase State
  const [units, setUnits] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [topDonors, setTopDonors] = useState<any[]>([]);

  useEffect(() => {
    const fetch = async () => {
      if (id) {
        const data = await api.getListingById(id);
        setListing(data);
        if (data) {
          api.trackEvent('listing_view', id, data.title);
          if (data.type === 'philanthropic') {
            const donors = await api.getTopDonors(id);
            setTopDonors(donors);
          }
        }
      }
      setLoading(false);
    };
    fetch();
  }, [id]);

  const handlePurchase = async () => {
    if (!listing || !user || !listing.unitPrice) return;
    
    setPurchasing(true);
    try {
      await api.createTransaction({
        userId: user.id,
        listingId: listing.id,
        listingTitle: listing.title,
        type: listing.type === 'micro_investment' ? 'investment' : 'donation',
        amount: units * listing.unitPrice,
        units: units,
      });
      
      setPurchaseSuccess(true);
      // Refresh listing to show updated unitsSold
      const updatedListing = await api.getListingById(listing.id);
      setListing(updatedListing);
      
      if (listing.type === 'philanthropic') {
        const donors = await api.getTopDonors(listing.id);
        setTopDonors(donors);
      }

      setTimeout(() => {
        setPurchaseSuccess(false);
        setUnits(1);
      }, 3000);
    } catch (error) {
      console.error('Purchase failed', error);
    } finally {
      setPurchasing(false);
    }
  };

  const handleStartChat = async () => {
    if (!listing || !isAuthenticated) {
      navigate('/login', { state: { from: location } });
      return;
    }

    setStartingChat(true);
    try {
      await api.startChat(listing.id);
      navigate('/dashboard', { state: { activeTab: 'chats', listingId: listing.id } });
    } catch (error) {
      const err = error;
      alert(err?.message || 'امکان شروع گفتگو وجود ندارد.');
    } finally {
      setStartingChat(false);
    }
  };

  const formatPriceCompact = (price: number) => {
      if (price >= 1000000000) {
          return `${(price / 1000000000).toFixed(1).replace(/\.0$/, '')} میلیارد تومان`;
      }
      if (price >= 1000000) {
          return `${(price / 1000000).toFixed(0)} میلیون تومان`;
      }
      return `${price.toLocaleString()} تومان`;
  };

  const renderBusinessMetrics = (listing: Listing) => {
    if (listing.type === 'sale') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
           <div className="bg-blue-50 p-4 rounded-xl text-center border border-blue-100">
              <div className="text-gray-500 text-xs mb-1">درآمد ماهانه</div>
              <div className="font-bold text-blue-700">{listing.monthlyRevenue ? listing.monthlyRevenue.toLocaleString() : '---'}</div>
           </div>
           <div className="bg-green-50 p-4 rounded-xl text-center border border-green-100">
              <div className="text-gray-500 text-xs mb-1">سود خالص</div>
              <div className="font-bold text-green-700">{listing.monthlyProfit ? listing.monthlyProfit.toLocaleString() : '---'}</div>
           </div>
           <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100">
              <div className="text-gray-500 text-xs mb-1">سال تاسیس</div>
              <div className="font-bold text-gray-800">{listing.foundedYear || '---'}</div>
           </div>
           <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100">
              <div className="text-gray-500 text-xs mb-1">پرسنل</div>
              <div className="font-bold text-gray-800">{listing.employeeCount || '---'} نفر</div>
           </div>
        </div>
      );
    } 
    
    if (listing.type === 'investment') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
           <div className="bg-purple-50 p-4 rounded-xl text-center border border-purple-100">
              <div className="text-gray-500 text-xs mb-1">سهام پیشنهادی</div>
              <div className="font-bold text-purple-700">{listing.equityOffered ? `%${listing.equityOffered}` : 'توافقی'}</div>
           </div>
           <div className="bg-purple-50 p-4 rounded-xl text-center border border-purple-100">
              <div className="text-gray-500 text-xs mb-1">بازگشت سرمایه (ROI)</div>
              <div className="font-bold text-purple-700">{listing.roiDuration ? `${listing.roiDuration} ماه` : '---'}</div>
           </div>
           <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100 hidden md:block">
              <div className="text-gray-500 text-xs mb-1">سرمایه کل</div>
              <div className="font-bold text-gray-800">{listing.price.toLocaleString()}</div>
           </div>
        </div>
      );
    }

    if (listing.type === 'demand') {
        return (
            <div className="grid grid-cols-2 gap-4 mb-8">
               <div className="bg-teal-50 p-4 rounded-xl flex items-center gap-3 border border-teal-100">
                  <Clock className="text-teal-600" size={24}/>
                  <div>
                    <div className="text-gray-500 text-xs">زمان‌بندی خرید</div>
                    <div className="font-bold text-teal-800">
                        {listing.purchaseTimeline === 'immediately' ? 'فوری' : 
                         listing.purchaseTimeline === '1_month' ? '۱ ماهه' : 'در حال بررسی'}
                    </div>
                  </div>
               </div>
            </div>
        )
    }

    if (listing.type === 'intangible') {
      return (
        <div className="space-y-4 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
             <div className="bg-indigo-50 p-4 rounded-xl text-center border border-indigo-100">
                <div className="text-gray-500 text-xs mb-1">نوع دارایی</div>
                <div className="font-bold text-indigo-700">
                  {listing.assetType === 'brand' ? 'برند' : 
                   listing.assetType === 'software' ? 'نرم‌افزار' : 
                   listing.assetType === 'patent' ? 'اختراع' : 
                   listing.assetType === 'trademark' ? 'نام تجاری' : 'سایر'}
                </div>
             </div>
             <div className="bg-indigo-50 p-4 rounded-xl text-center border border-indigo-100">
                <div className="text-gray-500 text-xs mb-1">شماره ثبت</div>
                <div className="font-bold text-indigo-700">{listing.registrationNumber || '---'}</div>
             </div>
             <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100">
                <div className="text-gray-500 text-xs mb-1">روش ارزش‌گذاری</div>
                <div className="font-bold text-gray-800">{listing.valuationMethod || '---'}</div>
             </div>
          </div>

          {/* Type Specific Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(listing.assetType === 'brand' || listing.assetType === 'trademark') && (
              <>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <span className="text-gray-500 text-sm">قلمرو جغرافیایی</span>
                  <span className="font-bold text-gray-800">{listing.brandTerritory || '---'}</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <span className="text-gray-500 text-sm">طبقه / کلاس</span>
                  <span className="font-bold text-gray-800">{listing.brandClass || '---'}</span>
                </div>
                {listing.domainName && (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center col-span-full">
                    <span className="text-gray-500 text-sm">نام دامنه</span>
                    <span className="font-bold text-teal-600">{listing.domainName}</span>
                  </div>
                )}
                {listing.socialMediaHandles && (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center col-span-full">
                    <span className="text-gray-500 text-sm">شبکه‌های اجتماعی</span>
                    <span className="font-bold text-gray-800">{listing.socialMediaHandles}</span>
                  </div>
                )}
              </>
            )}

            {listing.assetType === 'software' && (
              <>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <span className="text-gray-500 text-sm">تکنولوژی</span>
                  <span className="font-bold text-gray-800 truncate ml-2" title={listing.techStack}>{listing.techStack || '---'}</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <span className="text-gray-500 text-sm">نوع لایسنس</span>
                  <span className="font-bold text-gray-800">{listing.licenseType || '---'}</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <span className="text-gray-500 text-sm">کاربران فعال</span>
                  <span className="font-bold text-gray-800">{listing.activeUsers || '---'}</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <span className="text-gray-500 text-sm">مدل درآمدی</span>
                  <span className="font-bold text-gray-800">{listing.revenueModel || '---'}</span>
                </div>
                {listing.platform && (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                    <span className="text-gray-500 text-sm">پلتفرم</span>
                    <span className="font-bold text-gray-800">{listing.platform}</span>
                  </div>
                )}
                {listing.database && (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                    <span className="text-gray-500 text-sm">پایگاه داده</span>
                    <span className="font-bold text-gray-800">{listing.database}</span>
                  </div>
                )}
              </>
            )}

            {listing.assetType === 'patent' && (
              <>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <span className="text-gray-500 text-sm">وضعیت پتنت</span>
                  <span className="font-bold text-gray-800">
                    {listing.patentStatus === 'pending' ? 'در حال بررسی' : 
                     listing.patentStatus === 'granted' ? 'ثبت شده' : '---'}
                  </span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                  <span className="text-gray-500 text-sm">تاریخ انقضا</span>
                  <span className="font-bold text-gray-800">{listing.expirationDate || '---'}</span>
                </div>
                {listing.jurisdiction && (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                    <span className="text-gray-500 text-sm">حوزه قضایی</span>
                    <span className="font-bold text-gray-800">{listing.jurisdiction}</span>
                  </div>
                )}
                {listing.priorityDate && (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                    <span className="text-gray-500 text-sm">تاریخ اولویت</span>
                    <span className="font-bold text-gray-800">{listing.priorityDate}</span>
                  </div>
                )}
                {listing.filingDate && (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                    <span className="text-gray-500 text-sm">تاریخ اظهارنامه</span>
                    <span className="font-bold text-gray-800">{listing.filingDate}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    if (listing.type === 'micro_investment') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
           <div className="bg-emerald-50 p-4 rounded-xl text-center border border-emerald-100">
              <div className="text-gray-500 text-xs mb-1">حداقل سرمایه</div>
              <div className="font-bold text-emerald-700">{listing.minInvestmentAmount ? listing.minInvestmentAmount.toLocaleString() : '---'}</div>
           </div>
           <div className="bg-emerald-50 p-4 rounded-xl text-center border border-emerald-100">
              <div className="text-gray-500 text-xs mb-1">هدف کل</div>
              <div className="font-bold text-emerald-700">{listing.totalTargetAmount ? listing.totalTargetAmount.toLocaleString() : '---'}</div>
           </div>
           <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100">
              <div className="text-gray-500 text-xs mb-1">تعداد سرمایه‌گذار</div>
              <div className="font-bold text-gray-800">{listing.currentInvestorsCount || '۰'} نفر</div>
           </div>
           <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100">
              <div className="text-gray-500 text-xs mb-1">مدل سود</div>
              <div className="font-bold text-gray-800 truncate px-2">{listing.profitSharingModel || '---'}</div>
           </div>
        </div>
      );
    }

    if (listing.type === 'philanthropic') {
      return (
        <div className="space-y-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-rose-50 p-4 rounded-xl text-center border border-rose-100 col-span-full md:col-span-1">
                <div className="text-gray-500 text-xs mb-1">هدف پروژه</div>
                <div className="font-bold text-rose-700">{listing.projectGoal || '---'}</div>
            </div>
            <div className="bg-rose-50 p-4 rounded-xl text-center border border-rose-100">
                <div className="text-gray-500 text-xs mb-1">شاخص تاثیر</div>
                <div className="font-bold text-rose-700">{listing.socialImpact || '---'}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100">
                <div className="text-gray-500 text-xs mb-1">گزارش‌دهی</div>
                <div className="font-bold text-gray-800">{listing.reportingMethod || '---'}</div>
            </div>
          </div>

          {listing.recognitionLevels && listing.recognitionLevels.length > 0 && (
            <div className="bg-amber-50 p-6 rounded-2xl border border-amber-100">
              <h4 className="font-bold text-amber-900 mb-4 flex items-center gap-2">
                <Award size={18} /> سطوح قدردانی از خیرین
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {listing.recognitionLevels.map((level, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl border border-amber-200 shadow-sm">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-black text-amber-700">{level.label}</span>
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">
                        {level.minUnits} واحد
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 leading-relaxed">{level.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {topDonors.length > 0 && (
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Users size={18} /> خیرین برتر این پروژه
              </h4>
              <div className="space-y-3">
                {topDonors.slice(0, 5).map((donor, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-sm">
                        {donor.userName[0]}
                      </div>
                      <div>
                        <div className="font-bold text-sm text-gray-900">{donor.userName}</div>
                        <div className="text-[10px] text-rose-600 font-bold">{donor.levelLabel}</div>
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="font-black text-gray-900 text-sm">{donor.units} واحد</div>
                      <div className="text-[10px] text-gray-400">{donor.totalAmount.toLocaleString()} تومان</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  if (loading) return <div className="p-10 text-center">در حال بارگذاری...</div>;
  if (!listing) return <div className="p-10 text-center">آگهی یافت نشد.</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Right Column (Content) */}
        <div className="lg:col-span-2 space-y-8">
           {/* Image Gallery */}
           <div className="space-y-4">
             <div 
               className="rounded-3xl overflow-hidden shadow-xl h-[400px] relative bg-gray-100 cursor-pointer group"
               onClick={() => {
                 if (listing.imageUrls && listing.imageUrls.length > 0) {
                   setLightboxIndex(0);
                   setLightboxOpen(true);
                 }
               }}
             >
               <img 
                 src={listing.imageUrls && listing.imageUrls.length > 0 ? listing.imageUrls[0] : 'https://tbiz.ir/nemone.png'} 
                 alt={listing.title} 
                 className="w-full h-full object-contain bg-gray-50" 
                 referrerPolicy="no-referrer" 
               />
               <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
                 <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-gray-800">
                   کلیک برای بزرگنمایی
                 </div>
               </div>
               <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-sm font-bold text-gray-800 shadow-sm">
                  {listing.type === 'sale' ? 'فروش کسب‌وکار' : 
                   listing.type === 'investment' ? 'جذب سرمایه' : 
                   listing.type === 'demand' ? 'متقاضی خرید' : 
                   listing.type === 'investor' ? 'سرمایه‌گذار' :
                   listing.type === 'intangible' ? 'آگهی نامشهود' :
                   listing.type === 'micro_investment' ? 'سرمایه‌گذاری خرد' : 'عام‌المنفعه'}
               </div>
             </div>
             
             {/* Thumbnails */}
             {listing.imageUrls && listing.imageUrls.length > 1 && (
               <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
                 {listing.imageUrls.map((url, idx) => (
                   <div 
                     key={idx} 
                     className="w-24 h-24 rounded-xl overflow-hidden border-2 border-transparent hover:border-teal-500 cursor-pointer shrink-0 transition-all bg-gray-50"
                     onClick={() => {
                       setLightboxIndex(idx);
                       setLightboxOpen(true);
                     }}
                   >
                     <img src={url} className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                   </div>
                 ))}
               </div>
             )}

             {/* Videos */}
             {listing.videoUrls && listing.videoUrls.length > 0 && (
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                 {listing.videoUrls.map((url, idx) => (
                   <div key={idx} className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm aspect-video bg-black">
                     <video src={url} controls className="w-full h-full" />
                   </div>
                 ))}
               </div>
             )}
           </div>

           <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8">
             <div className="flex justify-between items-start mb-4">
                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-sm font-medium">{({
                  restaurant: 'رستوران',
                  startup: 'استارتاپ',
                  ecommerce: 'فروشگاه اینترنتی',
                  manufacturing: 'تولیدی',
                  medical: 'پزشکی',
                  education: 'آموزشی',
                  real_estate: 'املاک',
                  services: 'خدمات',
                  technology: 'فناوری',
                  other: 'سایر',
                } as Record<string, string>)[(listing.category || '').toLowerCase()] || listing.category}</span>
                <div className="flex items-center text-gray-400 text-sm gap-4">
                  <span className="flex items-center gap-1"><Eye size={16}/> {listing.views}</span>
                  <span className="flex items-center gap-1"><Calendar size={16}/> {new Date(listing.createdAt).toLocaleDateString('fa-IR')}</span>
                </div>
             </div>
             
             <h1 className="text-3xl font-black text-gray-900 mb-8 leading-tight">{listing.title}</h1>
             
             {/* Verification Badges */}
             <div className="flex flex-wrap gap-3 mb-8">
               {listing.status === 'approved' && listing.verificationDocsApproved && listing.hasValuationFile && (
                 <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-50 text-blue-700 border border-blue-100 text-sm font-bold">
                   <BarChart3 size={18} />
                   <span>دارای فایل ارزش‌گذاری</span>
                 </div>
               )}
               {listing.status === 'approved' && listing.verificationDocsApproved && listing.hasFinancialTurnoverFile && (
                 <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-50 text-green-700 border border-green-100 text-sm font-bold">
                   <FileText size={18} />
                   <span>دارای فایل گردش مالی</span>
                 </div>
               )}
               {listing.status === 'approved' && listing.verificationDocsApproved && listing.hasVerification3 && (
                 <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-50 text-purple-700 border border-purple-100 text-sm font-bold">
                   <ShieldCheck size={18} />
                   <span>تاییدیه حقوقی</span>
                 </div>
               )}
               {listing.status === 'approved' && listing.verificationDocsApproved && listing.hasVerification4 && (
                 <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 text-amber-700 border border-amber-100 text-sm font-bold">
                   <Check size={18} />
                   <span>تاییدیه فنی</span>
                 </div>
               )}
             </div>
             
             {/* Key Metrics Section */}
             {renderBusinessMetrics(listing)}

             <div className="prose max-w-none text-gray-600 leading-relaxed mb-8">
               <h3 className="text-xl font-bold text-gray-900 mb-4">درباره این فرصت</h3>
               <p className="whitespace-pre-wrap">{listing.description}</p>
             </div>

             {/* Tech Indicators (Only for relevant types) */}
             {(listing.type === 'sale' || listing.type === 'investment' || listing.type === 'micro_investment' || listing.type === 'philanthropic') && (
                 <div className="bg-gray-50 p-5 rounded-2xl text-center border border-gray-100 mb-8 max-w-md mx-auto">
                    <div className="text-4xl font-black text-teal-600 mb-2">{listing.trl}<span className="text-lg text-gray-300 font-normal">/9</span></div>
                    <div className="text-xs text-gray-500 mb-1">{TRL_LEVELS.find(l => l.id === listing.trl)?.label}</div>
                    <div className="text-sm font-bold text-gray-700">
                      {listing.type === 'philanthropic' ? 'آمادگی عملیاتی' : 'سطح فناوری (TRL)'}
                    </div>
                 </div>
             )}

             {listing.type === 'intangible' && listing.usageHistory && (
                <div className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100 mb-8">
                   <h4 className="font-bold text-indigo-900 mb-2 flex items-center gap-2"><Award size={18}/> سابقه بهره‌برداری</h4>
                   <p className="text-indigo-800 text-sm leading-relaxed">{listing.usageHistory}</p>
                </div>
             )}

             {listing.type === 'philanthropic' && listing.beneficiaries && (
                <div className="bg-rose-50 p-6 rounded-2xl border border-rose-100 mb-8">
                   <h4 className="font-bold text-rose-900 mb-2 flex items-center gap-2"><Heart size={18}/> ذینفعان پروژه</h4>
                   <p className="text-rose-800 text-sm leading-relaxed">{listing.beneficiaries}</p>
                </div>
             )}

             {listing.teamMembers && listing.teamMembers.length > 0 && (
               <div className="border-t border-gray-100 pt-8 mt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <div className="bg-teal-50 p-2 rounded-lg text-teal-600">
                        <Users size={20}/>
                    </div>
                    تیم ما
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {listing.teamMembers.map((member, i) => (
                      <div key={i} className="flex items-center gap-4 bg-gray-50 border border-gray-100 p-4 rounded-2xl hover:border-teal-200 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center text-teal-700 font-black text-lg shrink-0">
                          {member.name[0]}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-lg">{member.name}</p>
                          <p className="text-sm text-gray-500">{member.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
               </div>
             )}
           </div>
        </div>

        {/* Left Column (Sticky Sidebar) */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-6 border border-gray-100">
              {(listing.type === 'micro_investment' || listing.type === 'philanthropic') && listing.unitPrice ? (
                <div className="space-y-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="text-gray-500 text-sm mb-1">قیمت هر واحد</div>
                      <div className="text-2xl font-black text-teal-600 tracking-tight">
                        {formatPriceCompact(listing.unitPrice)}
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="text-gray-400 text-xs mb-1">ظرفیت کل</div>
                      <div className="font-bold text-gray-700">{listing.totalUnits?.toLocaleString() || '---'} واحد</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-gray-500">پیشرفت طرح</span>
                      <span className="font-bold text-teal-600">
                        {Math.round(((listing.unitsSold || 0) / (listing.totalUnits || 1)) * 100)}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-teal-500 transition-all duration-500" 
                        style={{ width: `${Math.min(100, ((listing.unitsSold || 0) / (listing.totalUnits || 1)) * 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                      <span>{listing.unitsSold || 0} واحد فروخته شده</span>
                      <span>{(listing.totalUnits || 0) - (listing.unitsSold || 0)} واحد باقی‌مانده</span>
                    </div>
                  </div>

                  {purchaseSuccess ? (
                    <div className="bg-green-50 border border-green-200 p-4 rounded-2xl text-center animate-bounce">
                      <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <Check size={24} />
                      </div>
                      <h4 className="font-black text-green-800 mb-1">پرداخت با موفقیت انجام شد</h4>
                      <p className="text-xs text-green-600">سهام شما در پنل کاربری قابل مشاهده است.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                        <label className="block text-xs font-bold text-gray-500 mb-3 text-center">تعداد واحد درخواستی</label>
                        <div className="flex items-center justify-between gap-4">
                          <button 
                            onClick={() => setUnits(Math.max(1, units - 1))}
                            className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 active:scale-95 transition-all"
                          >
                            -
                          </button>
                          <div className="text-2xl font-black text-gray-900">{units}</div>
                          <button 
                            onClick={() => setUnits(Math.min((listing.totalUnits || 0) - (listing.unitsSold || 0), units + 1))}
                            className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 active:scale-95 transition-all"
                          >
                            +
                          </button>
                        </div>
                        {listing.type === 'philanthropic' && (
                          <div className="mt-4 p-3 bg-orange-50 rounded-xl border border-orange-100 text-center">
                            <div className="text-[10px] text-orange-600 font-bold mb-1">امتیاز دریافتی شما</div>
                            <div className="text-lg font-black text-orange-800">{Math.floor(units / (listing.pointRatio || 100))} امتیاز</div>
                            <p className="text-[9px] text-orange-500 mt-1">به ازای هر {listing.pointRatio || 100} واحد، ۱ امتیاز دریافت می‌کنید.</p>
                          </div>
                        )}
                      </div>

                      <div className="pt-2 border-t border-dashed border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-gray-500 text-sm">مبلغ نهایی پرداخت:</span>
                          <span className="text-xl font-black text-gray-900">{(units * listing.unitPrice).toLocaleString()} <span className="text-xs font-normal text-gray-400">تومان</span></span>
                        </div>

                        {isAuthenticated ? (
                          <Button 
                            fullWidth 
                            size="lg" 
                            disabled={purchasing || (listing.unitsSold || 0) >= (listing.totalUnits || 0)}
                            onClick={handlePurchase}
                            className="py-4 rounded-2xl shadow-xl shadow-teal-600/20 flex items-center justify-center gap-2"
                          >
                            {purchasing ? 'در حال انتقال به درگاه...' : (
                              <>
                                <Coins size={20} />
                                {listing.type === 'micro_investment' ? 'خرید سهام و پرداخت' : 'پرداخت و حمایت مالی'}
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button 
                            fullWidth 
                            size="lg" 
                            variant="outline"
                            onClick={() => navigate('/login', { state: { from: location } })}
                            className="py-4 rounded-2xl border-2"
                          >
                            برای خرید وارد شوید
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="text-gray-500 text-sm mb-1">
                      {listing.type === 'investment' ? 'سرمایه مورد نیاز' : 
                       listing.type === 'demand' ? 'بودجه خرید' : 'قیمت کل'}
                  </div>
                  <div className="text-3xl font-black text-teal-600 mb-1 tracking-tight">
                    {formatPriceCompact(listing.price)}
                  </div>
                  <div className="text-xs text-gray-400 font-mono mb-6">
                      {listing.price.toLocaleString()} تومان
                  </div>
                  
                  {true && (
                    <>
                      <Button 
                        fullWidth 
                        size="lg" 
                        className="mb-3 flex items-center justify-center gap-2 rounded-xl py-4 shadow-lg shadow-teal-600/20"
                        onClick={() => setShowContactBox((prev) => !prev)}
                      >
                        <Phone size={20} /> اطلاعات تماس
                      </Button>
                      {showContactBox && (
                        <div className="mb-3 rounded-xl border border-teal-200 bg-teal-50 p-3 text-sm text-teal-800">
                          <div className="font-bold mb-1">شماره تماس</div>
                          <div dir="ltr" className="font-mono">
                            {listing.brokerageType === 'brokerage' ? '02191010764' : (listing.ownerPhone || 'شماره تماس ثبت نشده است')}
                          </div>
                        </div>
                      )}

                      {isAuthenticated ? (
                        <Button 
                            fullWidth 
                            variant="outline" 
                            onClick={handleStartChat}
                            disabled={startingChat}
                            className="mb-3 flex items-center justify-center gap-2 border-teal-600 text-teal-600 hover:bg-teal-50 rounded-xl py-3 border-2"
                        >
                            <MessageCircle size={18} /> {startingChat ? 'در حال شروع گفتگو...' : 'شروع گفتگو'}
                        </Button>
                      ) : (
                        <Button 
                            fullWidth 
                            variant="outline"
                            onClick={() => navigate('/login', { state: { from: location } })}
                            className="mb-3 flex items-center justify-center gap-2 rounded-xl py-3"
                        >
                            برای شروع گفتگو وارد شوید
                        </Button>
                      )}

                      <Button fullWidth variant="ghost" className="flex items-center justify-center gap-2 rounded-xl text-gray-500 hover:text-gray-900">
                        <Share2 size={18} /> اشتراک‌گذاری
                      </Button>
                    </>
                  )}

                  <div className="mt-6 flex items-start gap-3 bg-yellow-50 p-4 rounded-xl text-xs leading-5 text-yellow-800 border border-yellow-100">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    <p>پیش از انجام معامله و پرداخت وجه، از صحت اطلاعات اطمینان حاصل کنید. Tbiz مسئولیت حقوقی در قبال محتوای آگهی ندارد.</p>
                  </div>
                </>
              )}
            </div>

            <div className="bg-white rounded-3xl shadow-sm p-6 border border-gray-100">
              <h3 className="font-bold mb-4 flex items-center gap-2"><MapPin size={20}/> موقعیت مکانی</h3>
              <div className="flex items-center gap-2 text-gray-600 mb-4 bg-gray-50 p-2 rounded-lg">
                <span className="font-medium">{listing.location}</span>
              </div>
              <div className="h-40 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-200">
                نقشه
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxOpen && listing && listing.imageUrls && listing.imageUrls.length > 0 && (
        <ImageLightbox
          images={listing.imageUrls}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNext={() => setLightboxIndex((prev) => (prev + 1) % listing.imageUrls!.length)}
          onPrev={() => setLightboxIndex((prev) => (prev - 1 + listing.imageUrls!.length) % listing.imageUrls!.length)}
        />
      )}
    </div>
  );
};

export default ListingDetailPage;

